// Grid line operations utilities

/**
 * Update word assignments after grid line changes
 */
export const updateWordAssignments = (
  selectedTable,
  currentPage,
  newGridLines,
  pdfPageDimensions
) => {
  if (!selectedTable || !newGridLines?.[currentPage]) return selectedTable;

  const gridLines = newGridLines[currentPage];
  const sortedHorizontal = [...gridLines.horizontal].sort(
    (a, b) => a.y0 - b.y0
  );
  const sortedVertical = [...gridLines.vertical].sort((a, b) => a.x0 - b.x0);

  // Get all words from the current page
  const allWords = [];
  Object.keys(selectedTable).forEach((cellKey) => {
    if (cellKey.match(/^\d+_\d+$/)) {
      const cellLocationData = selectedTable[cellKey];
      if (cellLocationData && Array.isArray(cellLocationData)) {
        const firstCellInfo = cellLocationData[0];
        if (
          firstCellInfo &&
          firstCellInfo.page === currentPage &&
          firstCellInfo.words
        ) {
          firstCellInfo.words.forEach((word, wordIndex) => {
            allWords.push({
              ...word,
              originalCellKey: cellKey,
              wordIndex,
            });
          });
        }
      }
    }
  });

  // Create new cell assignments based on new grid lines
  const newCellAssignments = {};

  allWords.forEach((word) => {
    const bbox = word.bbox;
    if (!bbox) return;

    let wordCenterX, wordCenterY;
    const { width: pdfWidth, height: pdfHeight } = pdfPageDimensions.current;

    // Calculate word center position
    if (
      bbox.left !== undefined &&
      bbox.top !== undefined &&
      bbox.width !== undefined &&
      bbox.height !== undefined
    ) {
      if (
        bbox.left <= 1 &&
        bbox.top <= 1 &&
        bbox.width <= 1 &&
        bbox.height <= 1
      ) {
        wordCenterX = (bbox.left + bbox.width / 2) * pdfWidth;
        wordCenterY = (bbox.top + bbox.height / 2) * pdfHeight;
      } else {
        wordCenterX = bbox.left + bbox.width / 2;
        wordCenterY = bbox.top + bbox.height / 2;
      }
    } else {
      const x0 = bbox.x0 || bbox.x || 0;
      const y0 = bbox.y0 || bbox.y || 0;
      const x1 = bbox.x1 || bbox.right || 0;
      const y1 = bbox.y1 || bbox.bottom || 0;

      if (x0 <= 1 && y0 <= 1 && x1 <= 1 && y1 <= 1) {
        wordCenterX = ((x0 + x1) / 2) * pdfWidth;
        wordCenterY = ((y0 + y1) / 2) * pdfHeight;
      } else {
        wordCenterX = (x0 + x1) / 2;
        wordCenterY = (y0 + y1) / 2;
      }
    }

    // Determine row and column based on grid line positions
    let rowIndex = 0;
    let colIndex = 0;

    // Find row (between horizontal lines)
    for (let i = 0; i < sortedHorizontal.length; i++) {
      if (wordCenterY > sortedHorizontal[i].y0) {
        rowIndex = i + 1;
      }
    }

    // Find column (between vertical lines)
    for (let i = 0; i < sortedVertical.length; i++) {
      if (wordCenterX > sortedVertical[i].x0) {
        colIndex = i + 1;
      }
    }

    // Create cell key
    const cellKey = `${rowIndex}_${colIndex}`;

    if (!newCellAssignments[cellKey]) {
      newCellAssignments[cellKey] = [];
    }
    newCellAssignments[cellKey].push(word);
  });

  // Build new selectedTable structure
  const newSelectedTable = { ...selectedTable };

  // Clear existing cell assignments for current page
  Object.keys(newSelectedTable).forEach((cellKey) => {
    if (cellKey.match(/^\d+_\d+$/)) {
      const cellLocationData = newSelectedTable[cellKey];
      if (cellLocationData && Array.isArray(cellLocationData)) {
        const firstCellInfo = cellLocationData[0];
        if (firstCellInfo && firstCellInfo.page === currentPage) {
          // Clear words for this page
          newSelectedTable[cellKey] = [
            {
              ...firstCellInfo,
              words: [],
            },
          ];
        }
      }
    }
  });

  // Add new word assignments
  Object.keys(newCellAssignments).forEach((cellKey) => {
    const words = newCellAssignments[cellKey].sort((a, b) => {
      const aY = a.bbox?.top || a.bbox?.y0 || 0;
      const bY = b.bbox?.top || b.bbox?.y0 || 0;
      const aX = a.bbox?.left || a.bbox?.x0 || 0;
      const bX = b.bbox?.left || b.bbox?.x0 || 0;

      // First sort by Y position (top to bottom)
      if (Math.abs(aY - bY) > 5) {
        // Allow some tolerance for same line
        return aY - bY;
      }
      // Then by X position (left to right)
      return aX - bX;
    });

    if (!newSelectedTable[cellKey]) {
      newSelectedTable[cellKey] = [
        {
          page: currentPage,
          words: words,
        },
      ];
    } else {
      // Ensure the first element exists before setting words
      if (!newSelectedTable[cellKey][0]) {
        newSelectedTable[cellKey][0] = {
          page: currentPage,
          words: words,
        };
      } else {
        newSelectedTable[cellKey][0].words = words;
      }
    }
  });

  return newSelectedTable;
};

/**
 * Apply redistributed words from cellWordMap to selectedTable
 * Used after grid line movements to update word assignments with bbox data
 * @param {Object} selectedTable - The current selectedTable structure
 * @param {Object} cellWordMap - Map of cell keys to word arrays from redistribution
 * @param {number} redistributionPage - The page where redistribution occurred
 * @returns {Object} Updated selectedTable with new word assignments
 */
export const applyRedistributedWords = (
  selectedTable,
  cellWordMap,
  redistributionPage
) => {
  if (!cellWordMap || !redistributionPage) {
    return selectedTable;
  }

  const updatedSelectedTable = { ...selectedTable };

  // Build a set of cells that need to be cleared
  // We need to clear:
  // 1. Cells that had words extracted (source cells) - tracked via originalCellKey in words
  // 2. Cells that will receive redistributed words (destination cells) - keys in cellWordMap
  const cellsToClear = new Set();

  // Track destination cells (will be repopulated)
  Object.keys(cellWordMap).forEach((cellKey) => {
    const [rowStr, colStr] = cellKey.split("_");
    const tableRowIndex = parseInt(rowStr);
    const colIndex = parseInt(colStr);
    const selectedTableCellKey = `${tableRowIndex + 1}_${colIndex}`;
    cellsToClear.add(selectedTableCellKey);
  });

  // Track source cells (had words extracted)
  Object.values(cellWordMap).forEach((words) => {
    words.forEach((word) => {
      if (word.originalCellKey) {
        cellsToClear.add(word.originalCellKey);
      }
    });
  });

  // Clear ONLY the cells that were involved in redistribution
  cellsToClear.forEach((cellKey) => {
    if (updatedSelectedTable[cellKey]) {
      const cellData = updatedSelectedTable[cellKey];
      if (
        cellData &&
        Array.isArray(cellData) &&
        cellData[0] &&
        cellData[0].page === redistributionPage
      ) {
        updatedSelectedTable[cellKey] = [
          {
            ...cellData[0],
            words: [],
          },
        ];
      }
    }
  });

  // Then, assign redistributed words to their new cells
  Object.keys(cellWordMap).forEach((cellKey) => {
    const words = cellWordMap[cellKey];
    const sortedWords = words.sort((a, b) => {
      const aY = a.bbox?.top || a.bbox?.y0 || 0;
      const bY = b.bbox?.top || b.bbox?.y0 || 0;
      const aX = a.bbox?.left || a.bbox?.x0 || 0;
      const bX = b.bbox?.left || b.bbox?.x0 || 0;

      // First sort by Y position (top to bottom)
      if (Math.abs(aY - bY) > 5) {
        return aY - bY;
      }
      // Then by X position (left to right)
      return aX - bX;
    });

    // Convert tableData row index to selectedTable cell key
    // cellKey from cellWordMap is like "4_7" (tableData row 4, col 7)
    // We need to convert to selectedTable cell key "5_7" (row 5 for data row 4)
    const [rowStr, colStr] = cellKey.split("_");
    const tableRowIndex = parseInt(rowStr);
    const colIndex = parseInt(colStr);

    // For data rows, selectedTable cell key row index = tableData row index + 1
    const selectedTableCellKey = `${tableRowIndex + 1}_${colIndex}`;

    if (!updatedSelectedTable[selectedTableCellKey]) {
      updatedSelectedTable[selectedTableCellKey] = [
        {
          page: redistributionPage,
          words: sortedWords,
        },
      ];
    } else if (updatedSelectedTable[selectedTableCellKey][0]) {
      updatedSelectedTable[selectedTableCellKey][0].words = sortedWords;
    }
  });

  return updatedSelectedTable;
};
